import React, { useState, useCallback, useMemo, useEffect, useId, useRef } from "react";
import {
  View,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  Platform,
} from "react-native";
import Animated, { useSharedValue, useAnimatedStyle, withTiming, interpolateColor } from "react-native-reanimated";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { useResolvedId } from "@/hooks/useResolvedId";
import { SkeletonRow, SkeletonText } from "@/components/ui/Skeleton";
import Avatar from "@/components/Avatar";
import useDateTime from "@/hooks/useDateTime";
import { commentService, CommentTable, Comment, CommentMetaData } from "@/services/commentService";
import { supabase } from "@/lib/supabase-client";
import { Menu } from "@/components/Menu";
import { useModal } from "@/hooks/useModal";
import { ConfirmModal } from "@/components/Modals/ConfirmModal";
import { useFocusEffect } from "@react-navigation/native";

export interface CommentsProps {
  entityType: "projects" | "shifts" | "clients" | "users" | "contacts" | "investigations" | "incidents";
  entityId?: string;
  loading?: boolean;
  placeholder?: string;
  maxLength?: number;
  onCommentsUpdate?: (comments: Comment[]) => void;
}

export function Comments({
  entityType,
  entityId,
  loading: externalLoading = false,
  placeholder = "Add a comment...",
  maxLength = 500,
  onCommentsUpdate,
}: CommentsProps) {
  const { colors } = useTheme();
  const { user } = useAuth();
  const toast = useToast();
  const { formatDate, formatTime } = useDateTime();
  const { openModal, closeModal } = useModal();

  const uniqueInputId = useId();
  const resolvedId = useResolvedId("id");
  const id = entityId || resolvedId;

  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [internalLoading, setInternalLoading] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);

  // Edit mode state
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  // Background refresh state
  const isRefreshingRef = useRef(false);

  // Animated border for YouTube-style focus effect
  const borderProgress = useSharedValue(0);

  useEffect(() => {
    borderProgress.value = withTiming(isFocused ? 1 : 0, { duration: 200 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  const animatedBorderStyle = useAnimatedStyle(() => ({
    borderBottomColor: interpolateColor(borderProgress.value, [0, 1], [colors.border, colors.primary]),
    borderBottomWidth: borderProgress.value * 1 + 1,
  }));

  const isLoading = externalLoading || internalLoading;
  const showActions = isFocused || newComment.trim().length > 0;

  const handleCancel = useCallback(() => {
    setNewComment("");
    setIsFocused(false);
    inputRef.current?.blur();
  }, []);

  useEffect(() => {
    if (id === "undefined" || !id) return;
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, entityType]);

  const fetchComments = async () => {
    if (!id || id === "undefined") return;

    try {
      setInternalLoading(true);
      const data = await commentService.getComments(entityType as CommentTable, id);
      setComments(data);
      onCommentsUpdate?.(data);
    } catch (err: any) {
      toast.showToast({
        title: "Failed to fetch comments",
        message: err.message,
        toastType: "error",
      });
    } finally {
      setInternalLoading(false);
    }
  };

  // Silent background refresh - no loading state, no error toasts
  const refreshComments = useCallback(async () => {
    if (!id || id === "undefined" || isRefreshingRef.current) return;

    try {
      isRefreshingRef.current = true;
      const data = await commentService.getComments(entityType as CommentTable, id);
      setComments(data);
      onCommentsUpdate?.(data);
    } catch (err) {
      // Silent fail for background refresh
      console.warn("Background refresh failed:", err);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [id, entityType, onCommentsUpdate]);

  // Poll for new comments when screen is focused
  useFocusEffect(
    useCallback(() => {
      const interval = setInterval(() => {
        refreshComments();
      }, 15000); // 15 seconds

      return () => clearInterval(interval);
    }, [refreshComments]),
  );

  const handleAddComment = async () => {
    if (!newComment.trim() || submitting) return;

    try {
      setSubmitting(true);

      let commentInput: any = {
        message: newComment.trim(),
      };

      switch (entityType) {
        case "clients":
          commentInput.client_id = id;
          break;
        case "contacts":
          commentInput.contact_id = id;
          break;
        case "projects": {
          const { data: project } = await supabase.from("projects").select("client_id").eq("id", id).single();
          commentInput.project_id = id;
          commentInput.client_id = project?.client_id;
          break;
        }
        case "shifts": {
          const { data: shift } = await supabase.from("shifts").select("client_id, project_id").eq("id", id).single();
          commentInput.shift_id = id;
          commentInput.client_id = shift?.client_id;
          commentInput.project_id = shift?.project_id;
          break;
        }
        case "users":
          commentInput.user_id = id;
          break;
        case "investigations":
          commentInput.investigation_id = id;
          break;
        case "incidents":
          commentInput.incident_id = id;
          break;
      }

      const newCommentData = await commentService.addComment(entityType as CommentTable, commentInput);

      const updatedComments = [newCommentData, ...comments];
      setComments(updatedComments);
      onCommentsUpdate?.(updatedComments);
      setNewComment("");
      setIsFocused(false);
      inputRef.current?.blur();

      // Sync with server after a brief delay
      setTimeout(() => refreshComments(), 1000);
    } catch (err: any) {
      console.error("Failed to add comment:", err);
      toast.showToast({
        title: "Failed to add comment",
        message: err.message,
        toastType: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle like on a comment
  const handleToggleLike = useCallback(
    async (comment: Comment) => {
      if (!user?.id) return;

      const meta = comment.meta_data || {};
      const likes = meta.likes || [];
      const dislikes = meta.dislikes || [];

      const hasLiked = likes.includes(user.id);
      const hasDisliked = dislikes.includes(user.id);

      const newMeta: CommentMetaData = {
        ...meta,
        likes: hasLiked ? likes.filter((uid) => uid !== user.id) : [...likes, user.id],
        dislikes: hasDisliked ? dislikes.filter((uid) => uid !== user.id) : dislikes,
      };

      // Optimistic update
      const updatedComments = comments.map((c) => (c.id === comment.id ? { ...c, meta_data: newMeta } : c));
      setComments(updatedComments);

      try {
        await commentService.updateComment(entityType as CommentTable, comment.id, { meta_data: newMeta });
      } catch (err: any) {
        // Revert on error
        setComments(comments);
        toast.showToast({ title: "Failed to update", message: err.message, toastType: "error" });
      }
    },
    [comments, user?.id, entityType, toast],
  );

  // Toggle dislike on a comment
  const handleToggleDislike = useCallback(
    async (comment: Comment) => {
      if (!user?.id) return;

      const meta = comment.meta_data || {};
      const likes = meta.likes || [];
      const dislikes = meta.dislikes || [];

      const hasLiked = likes.includes(user.id);
      const hasDisliked = dislikes.includes(user.id);

      const newMeta: CommentMetaData = {
        ...meta,
        likes: hasLiked ? likes.filter((uid) => uid !== user.id) : likes,
        dislikes: hasDisliked ? dislikes.filter((uid) => uid !== user.id) : [...dislikes, user.id],
      };

      // Optimistic update
      const updatedComments = comments.map((c) => (c.id === comment.id ? { ...c, meta_data: newMeta } : c));
      setComments(updatedComments);

      try {
        await commentService.updateComment(entityType as CommentTable, comment.id, { meta_data: newMeta });
      } catch (err: any) {
        setComments(comments);
        toast.showToast({ title: "Failed to update", message: err.message, toastType: "error" });
      }
    },
    [comments, user?.id, entityType, toast],
  );

  // Start editing a comment
  const handleStartEdit = useCallback((comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.message);
  }, []);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingCommentId(null);
    setEditText("");
  }, []);

  // Save edited comment
  const handleSaveEdit = useCallback(
    async (comment: Comment) => {
      if (!editText.trim() || editText.trim() === comment.message) {
        handleCancelEdit();
        return;
      }

      // Build history with previous message
      const history = comment.meta_data?.history || [];
      history.push({
        message: comment.message,
        edited_at: new Date().toISOString(),
      });

      const newMeta: CommentMetaData = {
        ...comment.meta_data,
        edited: true,
        history,
      };

      // Optimistic update
      const updatedComments = comments.map((c) =>
        c.id === comment.id ? { ...c, message: editText.trim(), meta_data: newMeta } : c,
      );
      setComments(updatedComments);
      setEditingCommentId(null);
      setEditText("");

      try {
        await commentService.updateComment(entityType as CommentTable, comment.id, {
          message: editText.trim(),
          meta_data: newMeta,
        });
      } catch (err: any) {
        setComments(comments);
        toast.showToast({ title: "Failed to update", message: err.message, toastType: "error" });
      }
    },
    [comments, editText, entityType, toast, handleCancelEdit],
  );

  // Soft delete a comment (actual delete logic)
  const handleDelete = useCallback(
    async (comment: Comment) => {
      if (!user?.id || comment.author_id !== user.id) return;

      const newMeta: CommentMetaData = {
        ...comment.meta_data,
        deleted: true,
      };

      // Optimistic update
      const updatedComments = comments.map((c) => (c.id === comment.id ? { ...c, meta_data: newMeta } : c));
      setComments(updatedComments);
      closeModal();

      try {
        await commentService.updateComment(entityType as CommentTable, comment.id, { meta_data: newMeta });
      } catch (err: any) {
        setComments(comments);
        toast.showToast({ title: "Failed to delete", message: err.message, toastType: "error" });
      }
    },
    [comments, user?.id, entityType, toast, closeModal],
  );

  // Open delete confirmation modal
  const handleDeleteClick = useCallback(
    (comment: Comment) => {
      openModal(
        <ConfirmModal
          title="Delete Comment"
          message="Are you sure you want to delete this comment?"
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={() => handleDelete(comment)}
          onCancel={closeModal}
        />,
      );
    },
    [openModal, closeModal, handleDelete],
  );

  const formatDateTime = useCallback(
    (timestamp: string) => {
      return {
        date: formatDate(timestamp),
        time: formatTime(timestamp),
      };
    },
    [formatDate, formatTime],
  );

  const handleKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    const key = e.nativeEvent.key;
    const nativeEvent = e.nativeEvent as unknown as KeyboardEvent;
    if (key === "Enter" && !nativeEvent.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [comments]);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* YouTube-style Add Comment */}
      <View style={styles.addCommentSection}>
        <Avatar userId={user?.id} size={40} />
        <View style={styles.inputWrapper}>
          <Animated.View style={[styles.inputContainer, animatedBorderStyle]}>
            <TextInput
              ref={inputRef}
              id={`comment-input-${uniqueInputId}`}
              style={[styles.input, { color: colors.text }]}
              placeholder={placeholder}
              placeholderTextColor={colors.textSecondary}
              value={newComment}
              onChangeText={setNewComment}
              onFocus={() => setIsFocused(true)}
              onBlur={() => !newComment.trim() && setIsFocused(false)}
              multiline
              maxLength={maxLength}
              editable={!submitting}
              onKeyPress={handleKeyPress}
            />
          </Animated.View>

          {showActions && (
            <View style={styles.actionRow}>
              {newComment.length > maxLength * 0.7 && (
                <ThemedText
                  style={[
                    styles.charCounter,
                    { color: newComment.length >= maxLength ? colors.red?.text : colors.textSecondary },
                  ]}
                >
                  {newComment.length}/{maxLength}
                </ThemedText>
              )}
              <View style={styles.actionButtons}>
                <TouchableOpacity
                  onPress={handleCancel}
                  style={[styles.cancelButton, { backgroundColor: colors.backgroundSecondary }]}
                >
                  <ThemedText style={[styles.buttonText, { color: colors.text }]}>Cancel</ThemedText>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleAddComment}
                  disabled={!newComment.trim() || submitting}
                  style={[
                    styles.commentButton,
                    {
                      backgroundColor: newComment.trim() ? colors.primary : colors.backgroundSecondary,
                      opacity: submitting ? 0.6 : 1,
                    },
                  ]}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color={colors.background} />
                  ) : (
                    <ThemedText
                      style={[
                        styles.buttonText,
                        { color: newComment.trim() ? colors.background : colors.textSecondary },
                      ]}
                    >
                      Comment
                    </ThemedText>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>

      {/* Comments List */}
      <View style={styles.commentsList}>
        {isLoading ? (
          <View style={[styles.emptyState, { backgroundColor: colors.card }]}>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonText lines={2} widths={["80%", "60%"]} />
          </View>
        ) : comments.length === 0 ? (
          <View style={[styles.emptyState, { backgroundColor: colors.card }]}>
            <MaterialCommunityIcons name="comment-outline" size={32} color={colors.placeholder} />
            <ThemedText style={{ color: colors.placeholder, marginTop: 8 }}>No comments yet</ThemedText>
          </View>
        ) : (
          sortedComments.map((comment, index) => {
            const { date, time } = formatDateTime(comment.created_at);
            const isMe = comment.author_id === user?.id;
            const meta = comment.meta_data || {};
            const isDeleted = meta.deleted === true;
            const isEdited = meta.edited === true;
            const likes = meta.likes || [];
            const dislikes = meta.dislikes || [];
            const hasLiked = user?.id ? likes.includes(user.id) : false;
            const hasDisliked = user?.id ? dislikes.includes(user.id) : false;
            const isEditing = editingCommentId === comment.id;

            return (
              <View key={comment.id || index} style={styles.commentItem}>
                <Avatar userId={comment.author_id} size={40} />
                <View style={styles.commentContent}>
                  <View style={styles.commentHeader}>
                    <View style={styles.commentMeta}>
                      <ThemedText style={[styles.commentAuthor, { color: colors.text }]}>
                        {isMe
                          ? "You"
                          : `${comment.author_preferred_name || comment.author_first_name} ${comment.author_last_name}`}
                      </ThemedText>
                      <ThemedText style={[styles.commentTime, { color: colors.textSecondary }]}>
                        {date} {time}
                      </ThemedText>
                      {isEdited && !isDeleted && (
                        <ThemedText style={[styles.editedLabel, { color: colors.textSecondary }]}>(edited)</ThemedText>
                      )}
                    </View>
                    {isMe && !isDeleted && !isEditing && (
                      <Menu
                        buttonStyle={styles.menuButton}
                        hideArchive
                        hideDuplicate
                        onEdit={() => handleStartEdit(comment)}
                        onDelete={() => handleDeleteClick(comment)}
                      />
                    )}
                  </View>

                  {isDeleted ? (
                    <View style={[styles.deletedContainer, { backgroundColor: colors.backgroundSecondary }]}>
                      <MaterialCommunityIcons name="delete-outline" size={16} color={colors.textSecondary} />
                      <ThemedText style={[styles.deletedText, { color: colors.textSecondary }]}>
                        This comment has been deleted
                      </ThemedText>
                    </View>
                  ) : isEditing ? (
                    <View style={styles.editContainer}>
                      <TextInput
                        style={[
                          styles.editInput,
                          {
                            color: colors.text,
                            borderColor: colors.primary,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        value={editText}
                        onChangeText={setEditText}
                        multiline
                        maxLength={maxLength}
                        autoFocus
                      />
                      <View style={styles.editActions}>
                        <TouchableOpacity
                          onPress={handleCancelEdit}
                          style={[styles.editButton, { backgroundColor: colors.backgroundSecondary }]}
                        >
                          <ThemedText style={[styles.editButtonText, { color: colors.text }]}>Cancel</ThemedText>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => handleSaveEdit(comment)}
                          disabled={!editText.trim()}
                          style={[
                            styles.editButton,
                            {
                              backgroundColor: editText.trim() ? colors.primary : colors.backgroundSecondary,
                            },
                          ]}
                        >
                          <ThemedText
                            style={[
                              styles.editButtonText,
                              { color: editText.trim() ? colors.background : colors.textSecondary },
                            ]}
                          >
                            Save
                          </ThemedText>
                        </TouchableOpacity>
                      </View>
                    </View>
                  ) : (
                    <>
                      <ThemedText style={[styles.commentMessage, { color: colors.text }]}>{comment.message}</ThemedText>

                      {/* Actions: Like, Dislike */}
                      <View style={styles.commentActions}>
                        <TouchableOpacity
                          onPress={() => handleToggleLike(comment)}
                          style={styles.reactionButton}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <MaterialCommunityIcons
                            name={hasLiked ? "thumb-up" : "thumb-up-outline"}
                            size={16}
                            color={hasLiked ? colors.primary : colors.textSecondary}
                          />
                          {likes.length > 0 && (
                            <ThemedText style={[styles.reactionCount, { color: colors.textSecondary }]}>
                              {likes.length}
                            </ThemedText>
                          )}
                        </TouchableOpacity>

                        <TouchableOpacity
                          onPress={() => handleToggleDislike(comment)}
                          style={styles.reactionButton}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <MaterialCommunityIcons
                            name={hasDisliked ? "thumb-down" : "thumb-down-outline"}
                            size={16}
                            color={hasDisliked ? colors.red?.text : colors.textSecondary}
                          />
                          {dislikes.length > 0 && (
                            <ThemedText style={[styles.reactionCount, { color: colors.textSecondary }]}>
                              {dislikes.length}
                            </ThemedText>
                          )}
                        </TouchableOpacity>
                      </View>
                    </>
                  )}
                </View>
              </View>
            );
          })
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    gap: 24,
  },

  // Add Comment Section
  addCommentSection: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  inputWrapper: {
    flex: 1,
    gap: 8,
  },
  inputContainer: {
    borderBottomWidth: 1,
  },
  input: {
    fontSize: 14,
    lineHeight: 20,
    paddingVertical: 8,
    paddingHorizontal: 0,
    minHeight: 24,
    maxHeight: 120,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  actionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 4,
  },
  charCounter: {
    fontSize: 12,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 8,
    marginLeft: "auto",
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 18,
  },
  commentButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 18,
    minWidth: 90,
    alignItems: "center",
  },
  buttonText: {
    fontSize: 14,
    fontWeight: "500",
  },

  // Comments List
  commentsList: {
    gap: 16,
  },
  commentItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  commentContent: {
    flex: 1,
    gap: 4,
  },
  commentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  commentMeta: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  commentAuthor: {
    fontSize: 13,
    fontWeight: "600",
  },
  commentTime: {
    fontSize: 12,
  },
  editedLabel: {
    fontSize: 12,
    fontStyle: "italic",
  },
  menuButton: {
    backgroundColor: "transparent",
    borderWidth: 0,
    padding: 0,
    height: 24,
    width: 24,
  },
  commentMessage: {
    fontSize: 14,
    lineHeight: 20,
  },

  // Comment Actions
  commentActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    marginTop: 4,
  },
  reactionButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  reactionCount: {
    fontSize: 12,
  },

  // Edit mode
  editContainer: {
    gap: 8,
  },
  editInput: {
    fontSize: 14,
    lineHeight: 20,
    padding: 12,
    borderWidth: 2,
    borderRadius: 8,
    minHeight: 60,
    maxHeight: 150,
    textAlignVertical: "top",
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  editActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
  },
  editButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 16,
  },
  editButtonText: {
    fontSize: 13,
    fontWeight: "500",
  },

  // Deleted state
  deletedContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  deletedText: {
    fontSize: 13,
    fontStyle: "italic",
  },

  emptyState: {
    padding: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
  },
});

export default Comments;
